import React, { useEffect, useMemo, useState } from 'react';
import { MessageSquare, Search, Star, Filter, CalendarDays, Sparkles } from 'lucide-react';
import { fetchCustomerReviews } from '../api';

const Reviews = ({ reviews: initialReviews = [] }) => {
  const [reviews, setReviews] = useState(initialReviews);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [ratingFilter, setRatingFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const list = await fetchCustomerReviews();
        if (!cancelled) setReviews(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!cancelled) setReviews(Array.isArray(initialReviews) ? initialReviews : []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    return reviews.filter((r) => {
      const matchesQuery =
        !query ||
        r.customerName?.toLowerCase().includes(query.toLowerCase()) ||
        r.productName?.toLowerCase().includes(query.toLowerCase()) ||
        r.comment?.toLowerCase().includes(query.toLowerCase());
      const matchesRating =
        ratingFilter === 'all' || String(r.rating) === String(ratingFilter);
      return matchesQuery && matchesRating;
    });
  }, [reviews, query, ratingFilter]);

  const avgRating = filtered.length
    ? (filtered.reduce((sum, r) => sum + Number(r.rating || 0), 0) / filtered.length).toFixed(1)
    : '0.0';

  // Helper to get initials for avatar
  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  };

  // Nice background color lists for avatars
  const avatarColors = [
    'bg-emerald-50 text-emerald-700 border-emerald-100',
    'bg-purple-50 text-purple-700 border-purple-100',
    'bg-indigo-50 text-indigo-700 border-indigo-100',
    'bg-sky-50 text-sky-700 border-sky-100',
    'bg-rose-50 text-rose-700 border-rose-100',
    'bg-amber-50 text-amber-700 border-amber-100'
  ];

  const getAvatarColor = (name) => {
    if (!name) return avatarColors[0];
    const code = name.charCodeAt(0) + (name.charCodeAt(1) || 0);
    return avatarColors[code % avatarColors.length];
  };

  return (
    <div className="ui-page animate-fade-in">
      {/* HEADER SECTION */}
      <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight">Customer Reviews</h1>
          <p className="text-slate-500 mt-1 font-medium text-sm">Read and filter verified purchase feedback from customers.</p>
        </div>

        {/* Average rating card */}
        <div className="ui-card ui-card-hover px-5 py-4 border border-slate-100 flex items-center gap-4 bg-white shadow-sm min-w-[200px]">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center text-white shadow-md shadow-amber-400/20">
            <Sparkles size={18} />
          </div>
          <div>
            <p className="text-slate-400 text-[10px] font-bold uppercase tracking-wider">Average Rating</p>
            <div className="flex items-baseline gap-1 mt-0.5">
              <span className="text-2xl font-black text-slate-800">{avgRating}</span>
              <span className="text-slate-400 text-xs font-semibold">/ 5.0</span>
            </div>
          </div>
        </div>
      </div>

      {/* FILTER CONTROLS */}
      <div className="ui-card ui-card-pad mb-6 bg-white border border-slate-100 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2 relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by customer name, product, or review text..."
              className="w-full pl-10 pr-4 py-2.5 ui-input text-xs"
            />
          </div>
          <div className="relative">
            <Filter className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <select
              value={ratingFilter}
              onChange={(e) => setRatingFilter(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 ui-input text-xs bg-white cursor-pointer"
            >
              <option value="all">All Rating Levels</option>
              <option value="5">⭐⭐⭐⭐⭐ (5 Stars)</option>
              <option value="4">⭐⭐⭐⭐ (4 Stars)</option>
              <option value="3">⭐⭐⭐ (3 Stars)</option>
              <option value="2">⭐⭐ (2 Stars)</option>
              <option value="1">⭐ (1 Star)</option>
            </select>
          </div>
        </div>
      </div>

      {/* REVIEWS LIST */}
      <div className="grid grid-cols-1 gap-4">
        {loading && (
          <div className="ui-card p-12 text-center border border-slate-100 bg-white shadow-sm flex flex-col items-center justify-center">
            <div className="w-8 h-8 border-4 border-slate-100 border-t-emerald-600 rounded-full animate-spin mb-3" />
            <p className="text-slate-400 text-sm font-semibold">Retrieving review threads...</p>
          </div>
        )}

        {filtered.map((r) => {
          const colScheme = getAvatarColor(r.customerName);
          return (
            <div key={r.id} className="ui-card ui-card-pad ui-card-hover border border-slate-100/80 bg-white">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                
                {/* Customer Details */}
                <div className="flex items-start gap-3.5">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center font-black text-sm border ${colScheme} flex-shrink-0 shadow-sm`}>
                    {getInitials(r.customerName)}
                  </div>
                  <div>
                    <h3 className="font-extrabold text-slate-800 text-sm flex items-center gap-1.5">
                      {r.customerName}
                    </h3>
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-bold text-slate-400 uppercase">Product Purchase:</span>
                      <span className="inline-flex px-2 py-0.5 rounded-md text-[10.5px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-100/60 shadow-sm">
                        {r.productName || 'General Feedback'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Rating & Date */}
                <div className="flex items-center gap-4 self-start md:self-auto flex-wrap">
                  <div className="flex items-center gap-1 bg-amber-50/50 border border-amber-100/50 px-2.5 py-1 rounded-xl">
                    <div className="flex items-center gap-0.5 text-amber-500">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star
                          key={n}
                          className={`w-3.5 h-3.5 ${n <= Number(r.rating || 0) ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`}
                        />
                      ))}
                    </div>
                    <span className="text-xs font-black text-amber-700 ml-1">{r.rating}/5</span>
                  </div>

                  <div className="text-[11px] text-slate-400 font-bold flex items-center gap-1 bg-slate-50 border border-slate-100 px-2 py-1 rounded-xl">
                    <CalendarDays size={12} className="text-slate-400" />
                    {r.date || '—'}
                  </div>
                </div>
              </div>

              {/* Review Comment Bubble */}
              <div className="mt-4 p-4 bg-slate-50/70 border border-slate-100/70 rounded-2xl flex items-start gap-3 hover:bg-slate-50 transition-colors">
                <MessageSquare className="w-4 h-4 text-emerald-500/80 mt-0.5 flex-shrink-0" />
                <p className="text-slate-600 text-sm leading-relaxed font-medium">
                  {r.comment || 'No written commentary provided by user.'}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* EMPTY STATE */}
      {!loading && filtered.length === 0 && (
        <div className="ui-card p-12 text-center border border-slate-150 bg-white shadow-sm">
          <p className="text-slate-400 font-semibold text-sm">No review feedback found matching filter terms.</p>
        </div>
      )}
    </div>
  );
};

export default Reviews;
